/*
 * Size guide drawer — wires each guide (snippets/size-guide.liquid) to the
 * link that opens it. Opening, closing, focus trapping, Escape, click-outside,
 * scroll lock, focus restore and anchoring all come from the shared
 * OverlayPanel (assets/overlay-panel.js), the same one behind the header
 * navigation and the cart and search drawers; this panel anchors right, like
 * the cart.
 *
 * The link is the panel's toggle, so it gets aria-expanded and focus comes
 * back to it on close; the panel opens with focus on the close button. The
 * chart's scroll region is made focusable only while it actually scrolls
 * sideways, so keyboard users can scroll it without a stray tab stop when
 * the chart fits.
 */

// Bare name, resolved by the import map in layout/theme.liquid to the
// versioned asset URL (a relative './' path would bypass cache-busting).
import { OverlayPanel } from 'overlay-panel';

const init = (root) => {
  if (!root || root.dataset.sizeGuideReady) return;
  root.dataset.sizeGuideReady = '1';

  const dialog = root.querySelector('[data-size-guide-dialog]');
  const opener = root.id ? document.querySelector(`[data-size-guide-open][aria-controls="${CSS.escape(root.id)}"]`) : null;
  const scroller = root.querySelector('[data-size-guide-scroll]');
  const header = document.querySelector('[data-store-header]');
  // The header's icons group: on desktop the drawer opens level with it, over
  // the header, its right edge in line with the group's.
  const anchor = header ? header.querySelector('[data-header-actions]') || header.querySelector('[data-header-bar]') : null;

  const fitScroller = () => {
    if (!scroller) return;
    if (scroller.scrollWidth > scroller.clientWidth + 1) scroller.tabIndex = 0;
    else scroller.removeAttribute('tabindex');
  };

  const panel = new OverlayPanel({
    root,
    dialog,
    toggle: opener,
    anchor,
    origin: 'right',
    onOpen: fitScroller,
  });

  root.querySelectorAll('[data-size-guide-close]').forEach((closer) => {
    closer.addEventListener('click', () => panel.close());
  });

  if (opener) {
    opener.addEventListener('click', (event) => {
      event.preventDefault(); // the href is the no-script fallback
      panel.open();
    });
  }

  window.addEventListener('resize', () => {
    if (panel.isOpen) fitScroller();
  });

  /* A link to the guide's fragment (the no-script fallback, or a shared URL):
     open the drawer instead, and drop the fragment so the page's :target
     styling — the in-page fallback — doesn't apply as well. */
  if (root.id && window.location.hash === `#${root.id}`) {
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    panel.open();
  }
};

const initAll = () => document.querySelectorAll('[data-size-guide]').forEach(init);

if (document.readyState !== 'loading') initAll();
else document.addEventListener('DOMContentLoaded', initAll);

// Theme editor: re-wire a freshly reloaded section.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-size-guide]').forEach(init);
});
